import React, { FunctionComponent } from "react";
import { ToastContainer, toast, ToastContent, TypeOptions, ToastOptions } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import i18next from "i18next";
import { Icon } from "components/Icon";
import { colorState } from "components/Foundation";
import { ToastDiv } from "./styles";

type ToastPosition =
    | "top-right"
    | "top-center"
    | "top-left"
    | "bottom-right"
    | "bottom-center"
    | "bottom-left";

interface ToastProps {
    autoClose: number | false;
    closeOnClick: boolean;
    hideProgressBar: boolean;
    draggable: boolean;
    pauseOnHover: boolean;
    position: ToastPosition;
    theme?: "light" | "dark";
    type?: TypeOptions;
    toastId: string;
    icon?: boolean | React.ReactNode | string | number;
    customIcon?: React.ReactNode;
}

function getDefaultIcon(type?: TypeOptions) {
    switch (type) {
        case "info":
            return (
                <Icon
                    icon="info_icon"
                    stroke={colorState.icon.default["primary-inverse"]}
                    svgbackgroundColor="transparent"
                />
            );
        case "error":
            return (
                <Icon
                    icon="x_circle"
                    stroke={colorState.icon.default["primary-inverse"]}
                    svgbackgroundColor="transparent"
                />
            );
        case "success":
            return (
                <Icon
                    fill="none"
                    icon="check_circle"
                    isSmallIcon={true}
                    stroke={colorState.icon.success.primary}
                    svgbackgroundColor={colorState.surface.default.primary}
                />
            );
        case "warning":
            return (
                <Icon
                    icon="warning_icon"
                    stroke={colorState.icon.default.primary}
                    svgbackgroundColor="transparent"
                />
            );
        default:
            return null;
    }
}

function getToastId(type?: string, message?: string | number) {
    // Removing whitespace and replacing with -

    return `${type ?? "toast"}-${String(message ?? "")
        .trim()
        .toLowerCase()
        // eslint-disable-next-line require-unicode-regexp
        .replace(/\s+/g, "-")
        .slice(0, 10)}`;
}

export function showToast(
    message: ToastContent,
    options?: Partial<ToastProps>,
    timeout?: number
): string {
    const toastId =
        options?.toastId ??
        getToastId(
            options?.type,
            typeof message === "string" || typeof message === "number" ? message : ""
        );
    const toastOptions: ToastOptions = {
        ...ToastNotification.defaultProps,
        ...options,
        toastId,
        icon: options?.icon !== false && (options?.customIcon ?? getDefaultIcon(options?.type))
    };

    setTimeout(() => {
        if (typeof message === "string") {
            toast(i18next.t(message), { ...toastOptions });
        } else {
            toast(message, { ...toastOptions });
        }
    }, timeout ?? 0);

    return toastId;
}

export function dismissToast(toastId?: string): void {
    toast.dismiss(toastId);
}

export const ToastNotification: FunctionComponent<ToastProps> = ({
    autoClose,
    closeOnClick,
    position,
    pauseOnHover,
    hideProgressBar,
    draggable,
    theme
}) => (
    <ToastDiv>
        <ToastContainer
            autoClose={autoClose}
            closeButton={({ type }) => (
                <div className="d-flex">
                    <Icon
                        fill={
                            type === "warning"
                                ? colorState.icon.default.primary
                                : colorState.text["primary-inverse"]
                        }
                        icon="close_icon"
                        svgbackgroundColor="transparent"
                    />
                </div>
            )}
            closeOnClick={closeOnClick}
            draggable={draggable}
            hideProgressBar={hideProgressBar}
            pauseOnHover={pauseOnHover}
            position={position}
            theme={theme}
        />
    </ToastDiv>
);

ToastNotification.defaultProps = {
    autoClose: 5000,
    closeOnClick: true,
    draggable: true,
    hideProgressBar: true,
    pauseOnHover: true,
    position: toast.POSITION.BOTTOM_CENTER,
    theme: "dark"
};
